import React, { useState, useEffect } from 'react';
import { resolveImage } from '../utils/image';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiTrash2,
  FiPlus,
  FiMinus,
  FiShoppingBag,
  FiMapPin,
  FiCreditCard,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { paymentService, walletService } from '../services';
import type { SavedCard } from '../services/wallet.service';
import api from '../services/api';
import { ECARD_PAY_ENABLED } from '../config/ecard';
import { LoaderComponent, EmptyState } from '../components/ui';
import { PaymentIframeModal } from '../components/payments';

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-CV', {
    style: 'currency',
    currency: 'CVE',
    minimumFractionDigits: 0,
  }).format(value);

const CAPE_VERDE_CITIES = [
  'Praia', 'Mindelo', 'Sal (Espargos)', 'Santa Maria', 'Assomada',
  'Porto Novo', 'Tarrafal', 'Ribeira Grande', 'Ponta do Sol',
  'Calheta', 'Pedra Badejo', 'Cidade Velha',
];

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
  'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
  'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
];

const COUNTRIES = [
  { code: 'CV', name: 'Cabo Verde' },
  { code: 'PT', name: 'Portugal' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'PL', name: 'Poland' },
  { code: 'IE', name: 'Ireland' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AO', name: 'Angola' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'ST', name: 'São Tomé e Príncipe' },
  { code: 'SN', name: 'Senegal' },
  { code: 'GH', name: 'Ghana' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'AE', name: 'United Arab Emirates' },
];

type PaymentMethod = 'wallet' | 'card' | 'ecard';

interface DeliveryForm {
  nome: string;
  telefone: string;
  email: string;
  morada: string;
  cidade: string;
  pais: string;
  notas: string;
}

interface IframePaymentState {
  url: string;
  transactionId: string | null;
  merchantRef: string;
  orderId: string;
  successUrl: string;
  digitalEmail: string;
  hasDigital: boolean;
}

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { cart, cartTotal, itemCount, updateQuantity, removeFromCart, clearCart } =
    useCart();
  const { showSuccess, showError } = useToast();
  const { user, cliente } = useAuth();

  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  // eCards (cartões SISP guardados) — para pagamento direto com token
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string>('');
  // Erro do último pagamento — mostrado inline (o toast pode passar despercebido)
  const [paymentError, setPaymentError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [iframePayment, setIframePayment] = useState<IframePaymentState | null>(null);
  const [shippingFee, setShippingFee] = useState<number>(0);
  // Loja em modo LEVANTAMENTO (cantinas/balcões): sem morada, sem taxa
  const [isPickup, setIsPickup] = useState(false);
  const [shippingZones, setShippingZones] = useState<Array<{id: string; name: string; symbol: string; displayAmount: number; feeCVE: number}>>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<string>('');
  const [deliveryForm, setDeliveryForm] = useState<DeliveryForm>({
    nome: '',
    telefone: '',
    email: '',
    morada: '',
    cidade: '',
    pais: '',
    notas: '',
  });

  const isAllDigital = cart.length > 0 && cart.every((item) => {
    const t = (item as any).TIPO || (item as any).tipo || '';
    return t === 'DIGITAL';
  });

  const hasDigital = cart.some((item) => {
    const t = (item as any).TIPO || (item as any).tipo || '';
    return t === 'DIGITAL';
  });

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Checkout');
  }, []);

  // Pre-fill form with user profile data (works even if cliente is null)
  useEffect(() => {
    setDeliveryForm((prev) => ({
      ...prev,
      nome: prev.nome || cliente?.NOME || user?.name || '',
      telefone: prev.telefone || cliente?.TELEMOVEL || '',
      email: prev.email || user?.email || cliente?.EMAIL || '',
      morada: prev.morada || cliente?.MORADA || '',
      cidade: prev.cidade || cliente?.CIDADE || '',
    }));
  }, [cliente, user]);

  // Carregar eCards guardados (pagamento direto com token SISP)
  useEffect(() => {
    if (!user) { setSavedCards([]); return; }
    walletService.getCards()
      .then((cards) => {
        setSavedCards(cards);
        const def = cards.find((c) => c.isDefault === 'S') || cards[0];
        if (def) setSelectedCardId(def.id);
      })
      .catch(() => setSavedCards([]));
  }, [user]);

  // Fetch shipping fee / zones for this store
  useEffect(() => {
    const lojaId = (cart[0] as any)?.LOJA_ID || (cart[0] as any)?.lojaId || (cart[0] as any)?.pg_loja_ID;
    if (!lojaId) return;
    const allDigital = cart.every((i) => (i as any).TIPO === 'DIGITAL' || (i as any).tipo === 'DIGITAL');
    api.get(`/public/shop/store/${lojaId}`).then((res) => {
      const data = res.data?.data;
      const pickup = data?.modoEntrega === 'LEVANTAMENTO';
      setIsPickup(pickup);
      if (pickup) {
        setShippingZones([]);
        setShippingFee(0);
        return;
      }
      const zones: any[] = Array.isArray(data?.shippingZones) ? data.shippingZones : [];
      if (zones.length > 0 && !allDigital) {
        setShippingZones(zones);
        setSelectedZoneId(zones[0].id);
        setShippingFee(parseFloat(zones[0].feeCVE) || 0);
      } else {
        setShippingZones([]);
        const fee = parseFloat(data?.taxaEntrega) || 0;
        setShippingFee(allDigital ? 0 : fee);
      }
    }).catch(() => {});
  }, [cart]);

  const isFormValid = isAllDigital
    ? true
    : isPickup
      ? deliveryForm.nome.trim() !== '' && deliveryForm.telefone.trim() !== ''
      : deliveryForm.nome.trim() !== '' &&
        deliveryForm.telefone.trim() !== '' &&
        deliveryForm.morada.trim() !== '' &&
        deliveryForm.cidade.trim() !== '' &&
        (shippingZones.length === 0 || deliveryForm.pais.trim() !== '');

  const canSubmit = cart.length > 0 && isFormValid;

  const handleFormChange = (
    field: keyof DeliveryForm,
    value: string
  ): void => {
    setDeliveryForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setLoading(true);

    try {
      const session = paymentService.getSessionData();
      if (!session) {
        showError(t('checkout.sessionExpired'));
        navigate('/login');
        return;
      }

      // Store delivery data
      sessionStorage.setItem('checkoutDelivery', JSON.stringify(deliveryForm));

      const cartRef = `SHOP-${Date.now()}`;

      if (Number(cartTotal) <= 0) {
        showError(t('checkout.invalidCartValue'));
        setLoading(false);
        return;
      }

      // Step 1: Create shop order (pg_order_loja + pg_acesso_digital for digital items)
      let shopOrderId: string | null = null;
      let totalAmount = Number(cartTotal) + shippingFee;
      const lojaId = (cart[0] as any)?.LOJA_ID || (cart[0] as any)?.lojaId || (cart[0] as any)?.pg_loja_ID;
      if (lojaId) {
        try {
          const checkoutRes = await api.post('/public/shop/checkout', {
            lojaId,
            items: cart.map(i => ({ produtoId: i.ID, quantidade: i.QTD })),
            cliente: {
              nome: deliveryForm.nome || user?.name || cliente?.NOME || '',
              // Email: prioridade ao perfil do utilizador para garantir entrega correta
              email: user?.email || cliente?.EMAIL || deliveryForm.email || '',
              telefone: deliveryForm.telefone || cliente?.TELEMOVEL || '',
            },
            ...(selectedZoneId ? { shippingZoneId: selectedZoneId } : {}),
          });
          if (checkoutRes.data?.success) {
            shopOrderId = checkoutRes.data.data.orderId;
            // Use server-calculated total (includes shipping fee)
            const serverTotal = Number(checkoutRes.data.data.total);
            if (serverTotal > 0) totalAmount = serverTotal;
            const serverShipping = Number(checkoutRes.data.data.shippingFee);
            if (serverShipping >= 0) setShippingFee(serverShipping);
          }
        } catch (orderErr: any) {
          console.warn('[checkout] shop order creation failed:', orderErr?.message);
        }
      }

      if (!shopOrderId) {
        showError('Não foi possível criar a encomenda. O pagamento não foi iniciado.');
        setLoading(false);
        return;
      }


      // Step 2: Initiate payment (wallet or card)
      const cartDesc = cart.map(i => (i as any).NOME || (i as any).name || (i as any).titulo).filter(Boolean).join(', ') || 'Pagali Shop';
      const returnUrl = `${window.location.origin}/payments/result`;

      setPaymentError('');
      if (paymentMethod === 'ecard' && !selectedCardId) {
        showError('Seleciona um cartão guardado para pagar.');
        setLoading(false);
        return;
      }

      const result = await paymentService.initiatePayment({
        serviceId: shopOrderId,
        serviceType: 'PSHOP',
        amount: totalAmount,
        reference: cartRef,
        walletCardId: paymentMethod === 'wallet' ? 'wallet'
          : paymentMethod === 'ecard' ? selectedCardId
          : undefined,
        paymentMethod: paymentMethod === 'wallet' ? 'wallet'
          : paymentMethod === 'ecard' ? 'ecard'
          : undefined,
        descricao: `Pagali Shop - ${cartDesc}`.substring(0, 100),
        returnUrl,
      });

      // Para produtos digitais, usar sempre o email do perfil do utilizador (configurado no sistema)
      const digitalEmail = hasDigital
        ? (user?.email || cliente?.EMAIL || deliveryForm.email || '')
        : '';
      const digitalSuffix = hasDigital && digitalEmail
        ? `&digital=true&email=${encodeURIComponent(digitalEmail)}`
        : '';

      if (result.success) {
        if (paymentMethod === 'wallet' || paymentMethod === 'ecard') {
          clearCart();
          sessionStorage.removeItem('checkoutDelivery');
          showSuccess(t('checkout.paymentCompleted'));
          // Wallet path already activates digital in client.ts, but call failsafe anyway
          if (hasDigital && shopOrderId) {
            api.post(`/public/shop/order/${shopOrderId}/activate-digital`).catch(() => {});
          }
          navigate(`/payments/result?status=success${digitalSuffix}`);
        } else if (result.paymentUrl) {
          sessionStorage.setItem('pendingPaymentRef', result.merchantRef || '');
          if (hasDigital && shopOrderId) {
            sessionStorage.setItem('pendingDigitalOrderId', shopOrderId);
            sessionStorage.setItem('pendingDigitalEmail', digitalEmail);
          }
          setIframePayment({
            url: result.paymentUrl,
            transactionId: result.transactionId || null,
            merchantRef: result.merchantRef || '',
            orderId: shopOrderId,
            successUrl: `/payments/result?status=success${digitalSuffix}`,
            digitalEmail,
            hasDigital,
          });
        } else {
          showError(t('checkout.paymentUrlNotAvailable'));
        }
      } else {
        const msg = result.error || t('payments.paymentFailed');
        setPaymentError(msg);
        showError(msg);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setPaymentError(t('checkout.checkoutError'));
      showError(t('checkout.checkoutError'));
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <button
          onClick={() => navigate('/ecommerce')}
          className="flex items-center gap-2 text-pagali-gray-600 hover:text-pagali-gray-800 mb-6 transition-colors"
        >
          <FiArrowLeft />
          <span>{t('common.back')}</span>
        </button>
        <EmptyState
          icon={<FiShoppingBag className="text-4xl" />}
          title={t('checkout.emptyCart')}
          description={t('checkout.emptyCartDesc')}
          action={
            <button
              onClick={() => navigate('/ecommerce')}
              className="btn-primary"
            >
              {t('checkout.exploreStores')}
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-6 px-4 pb-32">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/ecommerce')}
          className="p-2 rounded-full hover:bg-pagali-gray-100 transition-colors"
        >
          <FiArrowLeft className="text-xl text-pagali-gray-700" />
        </button>
        <h1 className="text-xl font-bold text-pagali-gray-900">
          {t('checkout.title')}
        </h1>
      </div>

      {/* Step Indicators */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3].map((s) => (
          <button
            key={s}
            onClick={() => setStep(s)}
            className={`flex-1 h-1.5 rounded-full transition-colors ${
              s <= step ? 'bg-pagali-purple' : 'bg-pagali-gray-200'
            }`}
          />
        ))}
      </div>

      {/* Step 1 - Resumo do Pedido */}
      <div className="mb-4">
        <button
          onClick={() => setStep(step === 1 ? 0 : 1)}
          className="w-full flex items-center justify-between p-4 bg-white rounded-2xl shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-pagali-purple/10 flex items-center justify-center">
              <FiShoppingBag className="text-pagali-purple" />
            </div>
            <span className="font-semibold text-pagali-gray-800">
              {t('checkout.orderSummary')}
            </span>
            <span className="text-xs text-pagali-gray-500">
              ({itemCount} {itemCount === 1 ? 'item' : 'items'})
            </span>
          </div>
          {step === 1 ? (
            <FiChevronUp className="text-pagali-gray-400" />
          ) : (
            <FiChevronDown className="text-pagali-gray-400" />
          )}
        </button>

        {step === 1 && (
          <div className="mt-2 bg-white rounded-2xl shadow-sm p-4 space-y-3">
            {cart.map((item) => {
              const imageSrc = resolveImage(item.IMAGEM, item.MIME_TYPE);
              const price = item.PRECO_PROMOCIONAL || item.PRECO;
              const subtotal = price * item.QTD;

              return (
                <div
                  key={item.ID}
                  className="flex gap-3 p-3 bg-pagali-gray-50 rounded-xl"
                >
                  {/* Product Image */}
                  <div className="w-16 h-16 rounded-lg bg-white overflow-hidden flex-shrink-0">
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={item.NOME}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FiShoppingBag className="text-xl text-pagali-gray-300" />
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-pagali-gray-800 text-sm truncate">
                      {item.NOME}
                    </h4>
                    <p className="text-xs text-pagali-gray-500 mt-0.5">
                      {formatCurrency(price)} {t('common.each')}
                    </p>

                    {/* Quantity Controls */}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() =>
                            updateQuantity(item.ID, item.QTD - 1)
                          }
                          className="w-6 h-6 rounded-full bg-white border border-pagali-gray-200 flex items-center justify-center text-pagali-gray-600 hover:border-pagali-primary hover:text-pagali-primary transition-colors"
                        >
                          <FiMinus className="text-xs" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium">
                          {item.QTD}
                        </span>
                        <button
                          onClick={() =>
                            updateQuantity(item.ID, item.QTD + 1)
                          }
                          disabled={
                            item.STOCK !== undefined &&
                            item.QTD >= item.STOCK
                          }
                          className="w-6 h-6 rounded-full bg-white border border-pagali-gray-200 flex items-center justify-center text-pagali-gray-600 hover:border-pagali-primary hover:text-pagali-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FiPlus className="text-xs" />
                        </button>
                      </div>

                      <span className="font-semibold text-pagali-gray-900">
                        {formatCurrency(subtotal)}
                      </span>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeFromCart(item.ID)}
                    className="p-1.5 text-pagali-gray-400 hover:text-pagali-error transition-colors self-start"
                  >
                    <FiTrash2 className="text-sm" />
                  </button>
                </div>
              );
            })}

            {/* Shipping zone selector */}
            {!isAllDigital && shippingZones.length > 0 && (
              <div className="pt-3 border-t border-pagali-gray-100">
                <label className="block text-xs font-medium text-pagali-gray-600 mb-1.5">Destino de envio</label>
                <select
                  value={selectedZoneId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedZoneId(id);
                    const zone = shippingZones.find(z => z.id === id);
                    setShippingFee(zone ? parseFloat(String(zone.feeCVE)) || 0 : 0);
                  }}
                  className="w-full px-3 py-2.5 rounded-xl border border-pagali-gray-200 text-sm text-pagali-gray-800 focus:outline-none focus:ring-1 focus:ring-pagali-purple/40 focus:border-pagali-purple bg-white"
                >
                  {shippingZones.map(z => (
                    <option key={z.id} value={z.id}>
                      {z.name} — {z.symbol}{z.displayAmount}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Totals */}
            <div className="pt-3 border-t border-pagali-gray-100 space-y-2">
              <div className="flex justify-between text-sm text-pagali-gray-600">
                <span>{t('common.subtotal')}</span>
                <span>{formatCurrency(cartTotal)}</span>
              </div>
              {!isAllDigital && (
                <div className="flex justify-between text-sm text-pagali-gray-600">
                  <span>{t('checkout.deliveryFee')}</span>
                  {shippingZones.length > 0 ? (
                    (() => {
                      const z = shippingZones.find(z => z.id === selectedZoneId);
                      return z ? <span>{z.symbol}{z.displayAmount} <span className="text-pagali-gray-400 text-xs">({formatCurrency(shippingFee)})</span></span> : null;
                    })()
                  ) : shippingFee > 0 ? (
                    <span>{formatCurrency(shippingFee)}</span>
                  ) : (
                    <span className="text-pagali-success">{t('checkout.toCalculate')}</span>
                  )}
                </div>
              )}
              <div className="flex justify-between text-lg font-semibold text-pagali-gray-900 pt-2 border-t border-pagali-gray-100">
                <span>{t('common.total')}</span>
                <span>{formatCurrency(cartTotal + shippingFee)}</span>
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full py-3 bg-pagali-purple text-white font-medium rounded-xl hover:bg-pagali-purple-light transition-colors mt-2"
            >
              {t('common.continue')}
            </button>
          </div>
        )}
      </div>

      {/* Step 2 - Dados de Entrega */}
      <div className="mb-4">
        <button
          onClick={() => setStep(step === 2 ? 0 : 2)}
          className="w-full flex items-center justify-between p-4 bg-white rounded-2xl shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-pagali-purple/10 flex items-center justify-center">
              <FiMapPin className="text-pagali-purple" />
            </div>
            <span className="font-semibold text-pagali-gray-800">
              {isAllDigital ? 'Dados de Contacto' : isPickup ? 'Levantamento no Local' : t('checkout.deliveryData')}
            </span>
            {isFormValid && (
              <FiCheckCircle className="text-pagali-success text-sm" />
            )}
          </div>
          {step === 2 ? (
            <FiChevronUp className="text-pagali-gray-400" />
          ) : (
            <FiChevronDown className="text-pagali-gray-400" />
          )}
        </button>

        {step === 2 && (
          <div className="mt-2 bg-white rounded-2xl shadow-sm p-4 space-y-4">
            {isAllDigital ? (
              /* ── Produto Digital: contacto read-only, sem morada ── */
              <div className="space-y-4">
                <div className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 rounded-xl text-sm text-blue-700">
                  <FiCheckCircle className="flex-shrink-0" />
                  <span>Produto digital — acesso enviado por email</span>
                </div>

                <div>
                  <label className="block text-xs font-medium text-pagali-gray-500 mb-1.5">Nome</label>
                  <div className="w-full px-4 py-3 rounded-xl border border-pagali-gray-200 bg-pagali-gray-50 text-pagali-gray-800 text-sm">
                    {user?.name || cliente?.NOME || '—'}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-pagali-gray-500 mb-1.5">Telefone</label>
                  <div className="w-full px-4 py-3 rounded-xl border border-pagali-gray-200 bg-pagali-gray-50 text-pagali-gray-800 text-sm">
                    {cliente?.TELEMOVEL || '—'}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-pagali-gray-500 mb-1.5">Email</label>
                  <div className="w-full px-4 py-3 rounded-xl border border-pagali-gray-200 bg-pagali-gray-50 text-pagali-gray-800 text-sm">
                    {user?.email || cliente?.EMAIL || '—'}
                  </div>
                </div>
              </div>
            ) : (
              /* ── Produto Físico: formulário completo (ou só contacto, em levantamento) ── */
              <>
                {isPickup && (
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 rounded-xl text-sm text-amber-800">
                    <FiMapPin className="flex-shrink-0" />
                    <span>Levantamento no local — a encomenda é recolhida na loja, sem entrega.</span>
                  </div>
                )}
                {/* Nome completo */}
                <div>
                  <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
                    {t('checkout.fullName')} *
                  </label>
                  <input
                    type="text"
                    value={deliveryForm.nome}
                    onChange={(e) => handleFormChange('nome', e.target.value)}
                    placeholder={t('checkout.fullNamePlaceholder')}
                    className="w-full px-4 py-3 rounded-xl border border-pagali-gray-200 focus:border-pagali-primary focus:ring-1 focus:ring-pagali-primary outline-none text-pagali-gray-800 placeholder:text-pagali-gray-400 transition-colors"
                  />
                </div>

                {/* Telefone */}
                <div>
                  <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
                    {t('common.phone')} *
                  </label>
                  <input
                    type="tel"
                    value={deliveryForm.telefone}
                    onChange={(e) => handleFormChange('telefone', e.target.value)}
                    placeholder={t('checkout.phonePlaceholder')}
                    className="w-full px-4 py-3 rounded-xl border border-pagali-gray-200 focus:border-pagali-primary focus:ring-1 focus:ring-pagali-primary outline-none text-pagali-gray-800 placeholder:text-pagali-gray-400 transition-colors"
                  />
                </div>

                {/* Morada / país / cidade — só quando há entrega */}
                {!isPickup && (<>
                <div>
                  <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
                    {t('checkout.addressLabel')} *
                  </label>
                  <input
                    type="text"
                    value={deliveryForm.morada}
                    onChange={(e) => handleFormChange('morada', e.target.value)}
                    placeholder={t('checkout.addressPlaceholder')}
                    className="w-full px-4 py-3 rounded-xl border border-pagali-gray-200 focus:border-pagali-primary focus:ring-1 focus:ring-pagali-primary outline-none text-pagali-gray-800 placeholder:text-pagali-gray-400 transition-colors"
                  />
                </div>

                {/* País — só para lojas com zonas internacionais */}
                {shippingZones.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
                      País *
                    </label>
                    <select
                      value={deliveryForm.pais}
                      onChange={(e) => {
                        handleFormChange('pais', e.target.value);
                        handleFormChange('cidade', '');
                      }}
                      className="w-full px-4 py-3 rounded-xl border border-pagali-gray-200 focus:border-pagali-primary focus:ring-1 focus:ring-pagali-primary outline-none text-pagali-gray-800 transition-colors appearance-none bg-white"
                    >
                      <option value="">Selecionar país</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Cidade / Estado — dinâmico conforme o país */}
                <div>
                  <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
                    {deliveryForm.pais === 'United States' ? 'State' : t('checkout.city')} *
                  </label>
                  {shippingZones.length === 0 || deliveryForm.pais === 'Cabo Verde' ? (
                    <select
                      value={deliveryForm.cidade}
                      onChange={(e) => handleFormChange('cidade', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-pagali-gray-200 focus:border-pagali-primary focus:ring-1 focus:ring-pagali-primary outline-none text-pagali-gray-800 transition-colors appearance-none bg-white"
                    >
                      <option value="">{t('checkout.selectCity')}</option>
                      {CAPE_VERDE_CITIES.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  ) : deliveryForm.pais === 'United States' ? (
                    <select
                      value={deliveryForm.cidade}
                      onChange={(e) => handleFormChange('cidade', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-pagali-gray-200 focus:border-pagali-primary focus:ring-1 focus:ring-pagali-primary outline-none text-pagali-gray-800 transition-colors appearance-none bg-white"
                    >
                      <option value="">Select state</option>
                      {US_STATES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={deliveryForm.cidade}
                      onChange={(e) => handleFormChange('cidade', e.target.value)}
                      placeholder="City / Region"
                      className="w-full px-4 py-3 rounded-xl border border-pagali-gray-200 focus:border-pagali-primary focus:ring-1 focus:ring-pagali-primary outline-none text-pagali-gray-800 placeholder:text-pagali-gray-400 transition-colors"
                    />
                  )}
                </div>
                </>)}

                {/* Notas */}
                <div>
                  <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
                    {t('checkout.notesOptional')}
                  </label>
                  <textarea
                    value={deliveryForm.notas}
                    onChange={(e) => handleFormChange('notas', e.target.value)}
                    placeholder={t('checkout.notesPlaceholder')}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-pagali-gray-200 focus:border-pagali-primary focus:ring-1 focus:ring-pagali-primary outline-none text-pagali-gray-800 placeholder:text-pagali-gray-400 transition-colors resize-none"
                  />
                </div>
              </>
            )}

            <button
              onClick={() => setStep(3)}
              disabled={!isFormValid}
              className="w-full py-3 bg-pagali-purple text-white font-medium rounded-xl hover:bg-pagali-purple-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.continue')}
            </button>
          </div>
        )}
      </div>

      {/* Step 3 - Metodo de Pagamento */}
      <div className="mb-4">
        <button
          onClick={() => setStep(step === 3 ? 0 : 3)}
          className="w-full flex items-center justify-between p-4 bg-white rounded-2xl shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-pagali-purple/10 flex items-center justify-center">
              <FiCreditCard className="text-pagali-purple" />
            </div>
            <span className="font-semibold text-pagali-gray-800">
              {t('checkout.paymentMethod')}
            </span>
          </div>
          {step === 3 ? (
            <FiChevronUp className="text-pagali-gray-400" />
          ) : (
            <FiChevronDown className="text-pagali-gray-400" />
          )}
        </button>

        {step === 3 && (
          <div className="mt-2 bg-white rounded-2xl shadow-sm p-4 space-y-3">
            {/* Erro do último pagamento — inline, sempre visível */}
            {paymentError && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                <span className="text-red-500 mt-0.5">⚠️</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-700">Erro no pagamento</p>
                  <p className="text-xs text-red-600 mt-0.5">{paymentError}</p>
                </div>
              </div>
            )}
            {/* Wallet Option */}
            <button
              onClick={() => setPaymentMethod('wallet')}
              className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-colors ${
                paymentMethod === 'wallet'
                  ? 'border-pagali-purple bg-pagali-purple/5'
                  : 'border-pagali-gray-200 hover:border-pagali-gray-300'
              }`}
            >
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  paymentMethod === 'wallet'
                    ? 'border-pagali-purple'
                    : 'border-pagali-gray-300'
                }`}
              >
                {paymentMethod === 'wallet' && (
                  <div className="w-2.5 h-2.5 rounded-full bg-pagali-purple" />
                )}
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-pagali-gray-800">
                  {t('checkout.payWithWallet')}
                </p>
                <p className="text-xs text-pagali-gray-500 mt-0.5">
                  {t('checkout.walletInstantPayment')}
                </p>
              </div>
            </button>

            {/* Card Option */}
            <button
              onClick={() => setPaymentMethod('card')}
              className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-colors ${
                paymentMethod === 'card'
                  ? 'border-pagali-purple bg-pagali-purple/5'
                  : 'border-pagali-gray-200 hover:border-pagali-gray-300'
              }`}
            >
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  paymentMethod === 'card'
                    ? 'border-pagali-purple'
                    : 'border-pagali-gray-300'
                }`}
              >
                {paymentMethod === 'card' && (
                  <div className="w-2.5 h-2.5 rounded-full bg-pagali-purple" />
                )}
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-pagali-gray-800">
                  {t('checkout.payWithCard')}
                </p>
                <p className="text-xs text-pagali-gray-500 mt-0.5">
                  {t('checkout.cardRedirect')}
                </p>
              </div>
            </button>

            {/* eCard Option — cartões SISP guardados (pagamento direto com token) */}
            {ECARD_PAY_ENABLED && savedCards.length > 0 && (
              <div>
                <button
                  onClick={() => setPaymentMethod('ecard')}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-colors ${
                    paymentMethod === 'ecard'
                      ? 'border-pagali-purple bg-pagali-purple/5'
                      : 'border-pagali-gray-200 hover:border-pagali-gray-300'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'ecard'
                        ? 'border-pagali-purple'
                        : 'border-pagali-gray-300'
                    }`}
                  >
                    {paymentMethod === 'ecard' && (
                      <div className="w-2.5 h-2.5 rounded-full bg-pagali-purple" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-pagali-gray-800">Pagali eCard</p>
                    <p className="text-xs text-pagali-gray-500 mt-0.5">
                      Pagamento direto com o cartão guardado, sem inserir dados
                    </p>
                  </div>
                </button>
                {paymentMethod === 'ecard' && (
                  <div className="mt-2 ml-8 space-y-1.5">
                    {savedCards.map((card) => (
                      <button
                        key={card.id}
                        onClick={() => setSelectedCardId(card.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-sm transition-colors ${
                          selectedCardId === card.id
                            ? 'border-pagali-purple bg-pagali-purple/5 text-pagali-gray-800'
                            : 'border-pagali-gray-200 text-pagali-gray-600 hover:border-pagali-gray-300'
                        }`}
                      >
                        <span className="font-medium">{card.alias || card.cardType}</span>
                        <span>•••• {card.lastFourDigits}</span>
                        {card.isDefault === 'S' && (
                          <span className="ml-auto text-[10px] bg-pagali-purple/10 text-pagali-purple px-1.5 py-0.5 rounded-full">
                            padrão
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Shipping zone selector */}
            {!isAllDigital && shippingZones.length > 0 && (
              <div className="pt-3 border-t border-pagali-gray-100">
                <label className="block text-xs font-medium text-pagali-gray-600 mb-1.5">Destino de envio</label>
                <select
                  value={selectedZoneId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedZoneId(id);
                    const zone = shippingZones.find(z => z.id === id);
                    setShippingFee(zone ? parseFloat(String(zone.feeCVE)) || 0 : 0);
                  }}
                  className="w-full px-3 py-2.5 rounded-xl border border-pagali-gray-200 text-sm text-pagali-gray-800 focus:outline-none focus:ring-1 focus:ring-pagali-purple/40 focus:border-pagali-purple bg-white"
                >
                  {shippingZones.map(z => (
                    <option key={z.id} value={z.id}>
                      {z.name} — {z.symbol}{z.displayAmount}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Order Summary */}
            <div className="pt-3 border-t border-pagali-gray-100 space-y-2">
              <div className="flex justify-between text-sm text-pagali-gray-600">
                <span>Subtotal ({itemCount} items)</span>
                <span>{formatCurrency(cartTotal)}</span>
              </div>
              {!isAllDigital && (
                <div className="flex justify-between text-sm text-pagali-gray-600">
                  <span>{t('checkout.deliveryFee')}</span>
                  {shippingZones.length > 0 ? (
                    (() => {
                      const z = shippingZones.find(z => z.id === selectedZoneId);
                      return z ? <span>{z.symbol}{z.displayAmount} <span className="text-pagali-gray-400 text-xs">({formatCurrency(shippingFee)})</span></span> : null;
                    })()
                  ) : shippingFee > 0 ? (
                    <span>{formatCurrency(shippingFee)}</span>
                  ) : (
                    <span className="text-pagali-success">{t('checkout.toCalculate')}</span>
                  )}
                </div>
              )}
              <div className="flex justify-between text-lg font-semibold text-pagali-gray-900 pt-2 border-t border-pagali-gray-100">
                <span>{t('common.total')}</span>
                <span>{formatCurrency(cartTotal + shippingFee)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-pagali-gray-200 p-4 z-40">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm text-pagali-gray-600">{t('checkout.totalToPay')}</span>
            <span className="text-lg font-bold text-pagali-gray-900">
              {formatCurrency(cartTotal + shippingFee)}
            </span>
          </div>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            className="w-full py-3.5 bg-pagali-purple text-white font-semibold rounded-xl hover:bg-pagali-purple-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <LoaderComponent size="sm" />
            ) : (
              <>
                <FiCheckCircle />
                {t('checkout.confirmOrder')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* SISP Payment Iframe Modal */}
      {iframePayment && (
        <PaymentIframeModal
          isOpen={!!iframePayment}
          paymentUrl={iframePayment.url}
          transactionId={iframePayment.transactionId}
          onClose={() => {
            setIframePayment(null);
            navigate('/payments/result?status=failed');
          }}
          onPaymentComplete={(status) => {
            const paymentContext = iframePayment;
            setIframePayment(null);
            if (status === 'completed') {
              clearCart();
              sessionStorage.removeItem('checkoutDelivery');
              if (paymentContext?.hasDigital && paymentContext.orderId) {
                api.post(`/public/shop/order/${paymentContext.orderId}/activate-digital`).catch(() => {});
              }
              navigate(paymentContext?.successUrl || '/payments/result?status=success');
            } else {
              navigate('/payments/result?status=failed');
            }
          }}
        />
      )}
    </div>
  );
};

export default Checkout;
